import { useEffect, useRef } from 'react';
import { routes } from 'config/config';
import useQueryApiClient from 'utils/useQueryApiClient';
import { isInProtectedArea } from 'utils/protectedArea';
import PublicRoute from 'enums/publicRoutes';
import { clearCartStorage } from 'public/context/CartContext';

const ACTIVITY_KEY = 'lastActivityAt';
const TICK_MS = 15_000;
const EXPIRY_BUFFER_MS = 60_000;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'pointerdown',
  'keydown',
  'scroll',
  'touchstart',
];

export const useIdleLogout = (enabled: boolean, idleMinutes?: number) => {
  const hasActivity = useRef(false);
  const hasFired = useRef(false);

  const { appendData: idleLogout } = useQueryApiClient({
    request: {
      url: `/api/v2/logout`,
      method: 'POST',
    },
    onSuccess: (response) => {
      clearCartStorage();
      localStorage.removeItem('wasLoggedIn');
      sessionStorage.setItem('pendingLoginError', 'message.session_expired');

      if (response?.is_latvija_lv_user) {
        window.location.replace(routes.api.baseUrl + '/vpm/logout');
      } else {
        window.location.href = PublicRoute.LOGIN;
      }
    },
    onError: () => {
      hasFired.current = false;
    },
  });

  useEffect(() => {
    if (!enabled || !idleMinutes) {
      return;
    }

    hasFired.current = false;
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));

    const markActivity = () => {
      hasActivity.current = true;
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true })
    );

    const check = () => {
      if (hasActivity.current) {
        hasActivity.current = false;
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
        return;
      }

      const lastActivityAt = Number(localStorage.getItem(ACTIVITY_KEY));
      if (!lastActivityAt) {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
        return;
      }

      const isExpired =
        Date.now() - lastActivityAt > idleMinutes * 60_000 + EXPIRY_BUFFER_MS;

      if (isExpired && isInProtectedArea() && !hasFired.current) {
        hasFired.current = true;
        idleLogout();
      }
    };

    const interval = setInterval(check, TICK_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity)
      );
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, idleMinutes]);
};
